import { LoadingOutlined } from '@ant-design/icons';
import { T } from '@tolgee/react';
import { GetServerSideProps } from 'next';
import { useContext, useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import styled from 'styled-components';

import { CrossSvg, LogoSvg } from '../../../../../assets/icons';
import { MagicBackground } from '../../../../../assets/icons/MagicBackground';
import theme from '../../../../../assets/theme';
import { Button } from '../../../../../components/_atoms/Button';
import { IconWrapper } from '../../../../../components/_atoms/IconWrapper';
import MaxSizeAspectRatioContainer from '../../../../../components/_molecules/MaxSizeAspectRatioContainer';
import { MaxSizeAspectRatioContainerContext } from '../../../../../components/_molecules/MaxSizeAspectRatioContainer/MaxSizeAspectRatioContainer';
import { useBreakPoint } from '../../../../../hooks/useBreakPoint';
import useDeleteGame from '../../../../../hooks/useDeleteGame';
import useRouterMatch from '../../../../../hooks/useRouterMatch';
import { useHideIntercom } from '../../../../../providers/CustomIntercomStateProvider';
import NoAccess from '../../../../../providers/NoAccess';
import { getBaseUrl } from '../../../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../../../utils/localisationUtils';
import { configureStore } from '../../../../../utils/store';

const ScaleComponent = ({ hostUrl, scale }: { hostUrl: string; scale: number }) => {
  const { height = 0, width = 0, scale: existingScale = 0 } = useContext(
    MaxSizeAspectRatioContainerContext,
  );
  const [showIframe, setShowIframe] = useState(false);
  const [isLoadedIframe, setIsLoadedIframe] = useState(false);
  useEffect(() => {
    setTimeout(() => setShowIframe(true), 1000);
  }, []);

  const containerWidth = width / scale / existingScale;
  const containerHeight = height / scale / existingScale;
  return (
    <StaticContainer
      $width={containerWidth}
      $height={containerHeight}
      $scale={`scale(${scale})`}
    >
      <DeviceContainer>
        {!isLoadedIframe && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <LoadingOutlined style={{ fontSize: 100, color: theme.colors.orange }} spin />
          </div>
        )}
        {showIframe && (
          <iframe
            src={hostUrl}
            onLoad={() => setIsLoadedIframe(true)}
            title="View"
            frameBorder="0"
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </DeviceContainer>
    </StaticContainer>
  );
};
const StaticContainer = styled.div<{
  $scale: string;
  $height: number;
  $width: number;
}>`
  height: ${({ $height }) => $height}px;
  width: ${({ $width }) => $width}px;
  transform: ${({ $scale }) => $scale};
  transform-origin: top left;
  display: inline-flex;
  border-radius: 10px;
  overflow: hidden;
`;

const PreviewGamePage = () => {
  const { tenantId, gameId: previewGameId, router } = useRouterMatch();
  const { deleteGame } = useDeleteGame();
  const previewGameIdRef = useRef<string>();
  const isLargerThan600 = useBreakPoint(600);
  const screenRef = useRef<HTMLDivElement>(null);
  useHideIntercom();

  const [isVertical, setIsVertical] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        const height = screenRef.current?.clientHeight || 0;
        const width = screenRef.current?.clientWidth || 0;

        if (height > width) {
          setIsVertical(true);
        } else {
          setIsVertical(false);
        }
      });
    });

    ro.observe(screenRef.current as HTMLElement);

    return () => {
      ro.disconnect();
    };
  }, []);

  const [hostUrl, setHostUrl] = useState<string>(
    `${getBaseUrl()}/${tenantId}/games/${previewGameId}/live?isPreview=true`,
  );
  useEffect(() => {
    setHostUrl(`${getBaseUrl()}/${tenantId}/games/${previewGameId}/live?isPreview=true`);
    previewGameIdRef.current = previewGameId;
  }, [tenantId, previewGameId]);

  const exitPreview = () => {
    router.back();
  };

  useEffect(() => {
    return () => {
      if (previewGameIdRef.current) {
        deleteGame({ config: { gameId: previewGameIdRef.current } });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <BackButtonContainer>
        {isLargerThan600 && (
          <div
            style={{
              width: '90px',
              position: 'absolute',
              left: 10,
              top: 10,
              color: theme.colors.orange,
            }}
          >
            <LogoSvg />
          </div>
        )}
        <T keyName="classroom simulator" defaultValue="Classroom Simulator" />
        <BackButton
          type="ghost"
          styles={{ border: 'none' }}
          onClick={() => {
            exitPreview();
          }}
          icon={
            <IconWrapper height="20px" width="20px" color="black">
              <CrossSvg />
            </IconWrapper>
          }
          notext
        ></BackButton>
      </BackButtonContainer>
      <PageContainer ref={screenRef}>
        <BackgroundContainer>
          <MagicBackground />
        </BackgroundContainer>
        {!isVertical ? (
          <DesktopContainer $isVertical={isVertical}>
            {/* Titles Row */}

            <h2
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: 36,
                marginBottom: 0,
              }}
            >
              <T keyName="Student screen" defaultValue="Student screen" />
            </h2>

            <h2
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                fontSize: 36,
                marginBottom: 0,
              }}
            >
              <T keyName="Classroom screen" defaultValue="Classroom screen" />
            </h2>

            {/* Devices Row */}
            <MaxSizeAspectRatioContainer
              style={{ alignItems: 'center' }}
              aspect={{ width: 800, height: 600 }}
            >
              <ScaleComponent
                scale={0.7}
                hostUrl={`https://${configureStore().config.game.url}`}
              ></ScaleComponent>
            </MaxSizeAspectRatioContainer>
            <MaxSizeAspectRatioContainer
              style={{ alignItems: 'center' }}
              aspect={{ width: 800, height: 600 }}
            >
              <ScaleComponent scale={0.7} hostUrl={hostUrl}></ScaleComponent>
            </MaxSizeAspectRatioContainer>

            {/* Instruction Helpers Row */}
            <InstructionHelper>
              <T
                keyName="classroomSimulator.stepOne"
                defaultValue="Step 1. Type in the <strong>pin</strong> and then your <strong>name</strong> to join the lesson"
                params={{ strong: <strong /> }}
              />
            </InstructionHelper>
            <InstructionHelper $invertedColors={false}>
              <T
                keyName="classroomSimulator.stepTwo"
                defaultValue="Step 2. Click the orange button to <strong>start the lesson</strong>"
                params={{ strong: <strong /> }}
              />
            </InstructionHelper>
          </DesktopContainer>
        ) : (
          <MobileContainer>
            <MobileChromebookContainer>
              <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
                <h2
                  style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: 36,
                    marginBottom: 0,
                  }}
                >
                  <T keyName="Student screen" defaultValue="Student screen" />
                </h2>
              </div>
              <MaxSizeAspectRatioContainer aspect={{ width: 1920, height: 1080 }}>
                <ScaleComponent
                  scale={1.5}
                  hostUrl={`https://${configureStore().config.game.url}`}
                ></ScaleComponent>
              </MaxSizeAspectRatioContainer>
            </MobileChromebookContainer>
            <MobileChromebookContainer>
              <div style={{ width: '100%', padding: '20px 0px 5px 0px' }}>
                <h2
                  style={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: 36,
                    marginBottom: 0,
                  }}
                >
                  <T keyName="Classroom screen" defaultValue="Classroom screen" />
                </h2>
              </div>
              <MaxSizeAspectRatioContainer aspect={{ width: 1920, height: 1080 }}>
                <ScaleComponent scale={1.5} hostUrl={hostUrl}></ScaleComponent>
              </MaxSizeAspectRatioContainer>
            </MobileChromebookContainer>
          </MobileContainer>
        )}
      </PageContainer>
    </>
  );
};

const DeviceContainer = styled.div`
  height: 100%;
  width: 100%;
  border: 5px solid ${theme.colors.black};
  border-radius: 10px;
  background-color: black;
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.35);
  position: relative;
`;

// LoaderOverlay removed because we now display static instructions instead

const InstructionHelper = styled.div<{ $invertedColors?: boolean }>`
  position: relative;
  margin: 0 auto;
  width: fit-content;
  z-index: 2;
  background-color: ${({ $invertedColors }) =>
    $invertedColors ? theme.colors.white : theme.colors.orange};
  padding: 10px;
  border-radius: 10px;
  color: ${({ $invertedColors }) =>
    $invertedColors ? theme.colors.orange : theme.colors.white};
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  box-shadow: ${theme.effects.magicFloatingShadow};

  h3 {
    margin: 0;
    text-align: center;
    color: ${({ $invertedColors }) =>
      $invertedColors ? theme.colors.orange : theme.colors.white};
  }

  //After add a small div with absolute position to be a centered up arrow
  &::before {
    position: absolute;
    content: '';
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    display: block;
    width: 20px;
    height: 15px;
    background-color: ${({ $invertedColors }) =>
      $invertedColors ? theme.colors.white : theme.colors.orange};
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
`;
const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  opacity: 0.2;
  svg {
    height: 98vh; // 99 and 100 adds a scroll...
    width: 100vw;
  }
`;

const PageContainer = styled.div`
  position: relative;
  height: calc(100% - 51px);
  width: 100%;
  padding: 0px 20px 20px 20px;
  background: radial-gradient(
    145.25% 138.82% at 27.25% -63.82%,
    #ffc698 0%,
    #fff1e6 100%
  );

  //  background: radial-gradient(
  //   145.25% 138.82% at 27.25% -63.82%,
  //   rgb(0, 0, 0) 0%,
  //   rgb(21, 10, 0) 100%
  // );
  overflow: hidden;
`;

const BackButtonContainer = styled.div`
  z-index: 2;
  position: relative;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: ${theme.colors.white};
  padding: 10px 20px;

  box-shadow: ${theme.effects.magicFloatingShadow};
  font-size: 20px;
  font-weight: bold;
  text-align: center;
`;

const BackButton = styled(Button)`
  position: absolute;
  top: 6px;
  right: 10px;
  padding: 10px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-size: 20px;

  svg {
    margin: 0;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

const MobileChromebookContainer = styled.div`
  width: 100%;
  height: 100%;
  z-index: 1;
  display: grid;
  grid-template-rows: 70px calc(100% - 70px); // 70px for the header and fill the rest for the screens
`;
const DesktopContainer = styled.div<{ $isVertical?: boolean }>`
  display: grid;
  height: 100%;
  width: 100%;
  max-width: 1280px;
  margin: 20px auto 0px auto;
  padding: 0px 0px 20px 0px;
  gap: 10px;
  justify-content: center;
  align-items: end;
  grid-template-columns: 49% 49%;
  grid-template-rows: 70px minmax(200px, 2fr) auto;
`;

const MobileContainer = styled.div`
  display: grid;
  height: 100%;
  width: 100%;
  grid-template-rows: 49% 49%;
  padding-top: 20px;
  gap: 20px;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'private, s-maxage=300, stale-while-revalidate=3600',
  );
  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
    },
  };
};

const PreviewGamePageExport = () => {
  return (
    <NoAccess>
      <PreviewGamePage />
    </NoAccess>
  );
};
export default PreviewGamePageExport;
